/**
 * useSeo - Composable for managing SEO metadata across pages
 * 
 * This composable provides a consistent way to set page-level and entity-level
 * SEO metadata in the Public Front application.
 */

interface SeoOptions {
    title?: string
    description?: string
    keywords?: string
    ogImage?: string
    ogType?: string
    noIndex?: boolean
    canonicalUrl?: string
}

interface GlobalSeoConfig {
    titleSuffix: string
    defaultDescription: string
    defaultKeywords: string
    defaultOgImage: string
}

/**
 * Absolute URL of a page without query string or trailing slash, so that
 * `/ecole`, `/ecole/` and `/ecole?utm_source=x` all declare the same canonical page.
 */
export const buildCanonicalUrl = (siteUrl: string, path: string): string => {
    const origin = siteUrl.replace(/\/+$/, '')
    const cleanPath = path.length > 1 ? path.replace(/\/+$/, '') : path
    return `${origin}${cleanPath || '/'}`
}

/** Search engines cut meta descriptions beyond about this many characters. */
const META_DESCRIPTION_MAX_LENGTH = 160

/**
 * Description shortened at a word boundary to fit search results, with an ellipsis when cut
 * (article excerpts and back-office texts are often longer).
 */
export const toMetaDescription = (text: string): string => {
    const clean = text.replace(/\s+/g, ' ').trim()
    if (clean.length <= META_DESCRIPTION_MAX_LENGTH) return clean
    const cut = clean.slice(0, META_DESCRIPTION_MAX_LENGTH - 1)
    const lastSpace = cut.lastIndexOf(' ')
    return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.!?-]+$/, '')}…`
}

export const useSeo = () => {
    const config = useRuntimeConfig()
    const route = useRoute()
    const nuxtApp = useNuxtApp()

    // Cache for global SEO config
    const globalSeoConfig = useState<GlobalSeoConfig | null>('globalSeoConfig', () => null)

    /**
     * Fetch global SEO configuration from the API
     */
    const fetchGlobalSeoConfig = async (): Promise<GlobalSeoConfig> => {
        if (globalSeoConfig.value) {
            return globalSeoConfig.value
        }

        try {
            const data = await $fetch<GlobalSeoConfig>(`${config.public.apiBase}/oxoweb/school/seo`)
            globalSeoConfig.value = data
            return data
        } catch (error) {
            console.error('Failed to fetch global SEO config:', error)
            return {
                titleSuffix: ' | OXO',
                defaultDescription: '',
                defaultKeywords: '',
                defaultOgImage: ''
            }
        }
    }

    /**
     * Set page SEO metadata
     * Automatically appends global title suffix and uses defaults where not provided
     */
    const setPageSeo = async (options: SeoOptions = {}) => {
        const global = await fetchGlobalSeoConfig()

        const fullTitle = options.title
            ? `${options.title}${global.titleSuffix || ''}`
            : `OXO${global.titleSuffix || ''}`

        const description = toMetaDescription(options.description || global.defaultDescription)
        const keywords = options.keywords || global.defaultKeywords
        const ogImage = options.ogImage || global.defaultOgImage
        const canonicalUrl = options.canonicalUrl || buildCanonicalUrl(config.public.siteUrl, route.path)

        // Build meta array
        const meta: any[] = [
            { name: 'description', content: description },
            { property: 'og:title', content: fullTitle },
            { property: 'og:description', content: description },
            { property: 'og:type', content: options.ogType || 'website' },
            { property: 'og:url', content: canonicalUrl },
            { name: 'twitter:title', content: fullTitle },
            { name: 'twitter:description', content: description }
        ]

        // Add keywords if present
        if (keywords) {
            meta.push({ name: 'keywords', content: keywords })
        }

        // Add OG image if present
        if (ogImage) {
            meta.push(
                { property: 'og:image', content: ogImage },
                { name: 'twitter:image', content: ogImage }
            )
        }

        // Add noindex if specified
        if (options.noIndex) {
            meta.push({ name: 'robots', content: 'noindex, nofollow' })
        }

        // Apply SEO using useHead
        nuxtApp.runWithContext(() => {
            useHead({
                title: fullTitle,
                meta,
                // Declared inline so `rel` keeps its literal type, which Unhead requires
                link: [{ rel: 'canonical', href: canonicalUrl }]
            })
        })
    }

    /**
     * Helper for article/news pages
     */
    const setArticleSeo = async (article: {
        title: string
        excerpt?: string
        metaTitle?: string
        metaDescription?: string
        keywords?: string
        ogImage?: string
        image?: string
        noIndex?: boolean
        date?: string
    }) => {
        await setPageSeo({
            title: article.metaTitle || article.title,
            description: article.metaDescription || article.excerpt || '',
            keywords: article.keywords,
            ogImage: article.ogImage || article.image,
            ogType: 'article',
            noIndex: article.noIndex
        })
    }

    /**
     * Helper for formation pages
     */
    const setFormationSeo = async (formation: {
        title: string
        description?: string
        metaTitle?: string
        metaDescription?: string
        keywords?: string
        ogImage?: string
        image?: string
    }) => {
        await setPageSeo({
            title: formation.metaTitle || formation.title,
            description: formation.metaDescription || formation.description || '',
            keywords: formation.keywords,
            ogImage: formation.ogImage || formation.image,
            ogType: 'website'
        })
    }

    /**
     * Helper for project pages
     */
    const setProjectSeo = async (project: {
        title: string
        description?: string
        metaTitle?: string
        metaDescription?: string
        keywords?: string
        ogImage?: string
        image?: string
    }) => {
        await setPageSeo({
            title: project.metaTitle || project.title,
            description: project.metaDescription || project.description || '',
            keywords: project.keywords,
            ogImage: project.ogImage || project.image,
            ogType: 'website'
        })
    }

    /**
     * Helper for event pages
     */
    const setEventSeo = async (event: {
        title: string
        description?: string
        metaTitle?: string
        metaDescription?: string
        keywords?: string
        ogImage?: string
        image?: string
        date?: string
    }) => {
        await setPageSeo({
            title: event.metaTitle || event.title,
            description: event.metaDescription || event.description || '',
            keywords: event.keywords,
            ogImage: event.ogImage || event.image,
            // Open Graph has no `event` type
            ogType: 'website'
        })
    }

    return {
        fetchGlobalSeoConfig,
        setPageSeo,
        setArticleSeo,
        setFormationSeo,
        setProjectSeo,
        setEventSeo
    }
}
